import { useState } from 'react';
import { C, DEGRADE_ACTION } from '../../theme';
import { Case } from './base';
import { BoutonDeuxGestes } from './BoutonDeuxGestes';
import { api } from '../../api/routes';
import { messageDErreur } from '../../api/useDonnees';
import type { Basculer } from '../cocher';
import type { ElementChecklist } from '../../api/types';

/**
 * Une ligne de liste : la case, le libelle, et le retrait.
 *
 * Le meme trio etait recopie aux trois endroits ou une liste s'affiche (checklists
 * d'evenement, section Listes du Planning, listes de courses de Repas) — il vit
 * desormais ici. Le retrait demande DEUX gestes : un pouce qui glisse ne supprime rien.
 */
export function LigneElement({
  element,
  basculer,
  signaler,
  rafraichir,
  taille = 20,
}: {
  element: ElementChecklist;
  basculer: Basculer;
  signaler: (message: string) => void;
  rafraichir: () => void;
  taille?: number;
}) {
  const [envoi, setEnvoi] = useState(false);
  const [renommage, setRenommage] = useState(false);
  const [nouveauLibelle, setNouveauLibelle] = useState(element.label);
  const fait = element.doneAt !== null;

  const renommer = async () => {
    const label = nouveauLibelle.trim();
    if (label === '' || label === element.label) {
      setRenommage(false);
      return;
    }
    if (envoi) return;
    setEnvoi(true);
    try {
      await api.renommerElement(element.id, label);
      setRenommage(false);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  const retirer = async () => {
    if (envoi) return;
    setEnvoi(true);
    try {
      await api.supprimerElement(element.id);
      rafraichir();
    } catch (cause) {
      signaler(messageDErreur(cause));
    } finally {
      setEnvoi(false);
    }
  };

  if (renommage) {
    return (
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void renommer();
        }}
        style={{ display: 'flex', gap: 6 }}
      >
        <input
          value={nouveauLibelle}
          onChange={(e) => setNouveauLibelle(e.target.value)}
          aria-label={`Nouveau libellé de : ${element.label}`}
          autoFocus
          style={{
            flex: 1,
            minWidth: 0,
            height: 34,
            border: `1px solid ${C.violetBordure}`,
            borderRadius: 9,
            padding: '0 10px',
            fontSize: 16,
            fontFamily: 'inherit',
            color: C.encre,
            // Sans fond declare, un input prend le BLANC du navigateur — et l'encre de
            // Colette etant claire, on n'y voit plus ce qu'on tape.
            background: C.carte,
            outline: 'none',
          }}
        />
        <button
          type="submit"
          className="pressable"
          disabled={envoi}
          style={{
            height: 34,
            padding: '0 10px',
            border: 'none',
            borderRadius: 9,
            background: envoi ? C.inactif : DEGRADE_ACTION,
            color: '#FFFFFF',
            fontSize: 12.5,
            fontWeight: 800,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          OK
        </button>
        <button
          type="button"
          className="pressable"
          aria-label={`Annuler le renommage de : ${element.label}`}
          onClick={() => {
            setNouveauLibelle(element.label);
            setRenommage(false);
          }}
          style={{
            height: 34,
            padding: '0 8px',
            border: `1px solid ${C.bordure}`,
            borderRadius: 9,
            background: C.carte,
            color: C.secondaire,
            fontSize: 12.5,
            fontWeight: 700,
            fontFamily: 'inherit',
            cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </form>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <button
        type="button"
        className="pressable"
        aria-pressed={fait}
        aria-label={`${fait ? 'Décocher' : 'Cocher'} : ${element.label}`}
        onClick={() => basculer('element', element.id, !fait)}
        style={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: 'none',
          border: 'none',
          padding: 0,
          fontFamily: 'inherit',
          cursor: 'pointer',
          textAlign: 'left',
        }}
      >
        <Case cochee={fait} taille={taille} />
        <span
          style={{
            fontSize: 14,
            color: fait ? C.secondaire : C.encre,
            textDecoration: fait ? 'line-through' : 'none',
          }}
        >
          {element.label}
        </span>
      </button>
      <button
        type="button"
        className="pressable"
        disabled={envoi}
        aria-label={`Renommer : ${element.label}`}
        onClick={() => {
          setNouveauLibelle(element.label);
          setRenommage(true);
        }}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          fontSize: 13,
          fontWeight: 800,
          lineHeight: 1,
          fontFamily: 'inherit',
          color: C.tertiaire,
          cursor: envoi ? 'default' : 'pointer',
        }}
      >
        ✎
      </button>
      <BoutonDeuxGestes
        libelle="×"
        libelleArme="Retirer ?"
        aria={`Retirer : ${element.label}`}
        ariaArme={`Confirmer le retrait : ${element.label}`}
        occupe={envoi}
        onConfirmer={() => void retirer()}
        style={{
          flexShrink: 0,
          background: 'none',
          border: 'none',
          padding: '2px 4px',
          fontSize: 16,
          fontWeight: 800,
          lineHeight: 1,
          fontFamily: 'inherit',
          color: C.tertiaire,
          cursor: envoi ? 'default' : 'pointer',
        }}
        styleArme={{ color: C.corailTexte, fontSize: 11.5 }}
      />
    </div>
  );
}
